import React, { useEffect, useRef, useState } from "react";
import styles from "../../styles/Evaluation/EvaluationForm.module.css";
import { base_url } from "../../ReuableFunctions/Blogs";
import Message from "../../UsefulCompoments/Message";
import Loading from "../Loading/Loading";
import { pdf } from "@react-pdf/renderer";
import { MyDocument } from "../../UsefulCompoments/MyDocument";
import {
  createOrder,
  paymentAmount,
  sendTransactionData,
  verifyPayment,
} from "../../ReuableFunctions/Payment";
import PaymentModal from "../Modal/PaymentModal";

function isValidURL(url) {
  const regex =
    /^(https?|ftp):\/\/([a-z0-9-]+\.)+[a-z0-9-]{2,6}(:[0-9]{1,5})?(\/[^\s]*)?$/i;
  return regex.test(url);
}
function isValidEmail(email) {
  const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return regex.test(email);
}
function isValidPhoneNumber(phoneNumber) {
  if (typeof phoneNumber !== "string") {
    return false; // Not a string
  }
  // Regex to match a valid phone number with country code
  const regex = /^\+[0-9]{1,3}[0-9]{10}$/;
  return regex.test(phoneNumber);
}
let initialFormState = {
  founderName: "",
  evaluationType: "PAID",
  founderEmail: "",
  address: "",
  startupName: "",
  contactNumber: "",
  startupStage: "",
  burnRate: "",
  currentRevenueUsd: "",
  runwayLeftMonths: "",
  seedCapital: "",
  hasCoFounder: "false",
  mvpBuilt: "false",
  validatedWithCustomers: "false",
  marketResearchDone: "false",
  idealCustomerIdentified: "false",
  hasPayingCustomers: "false",
  targetMarketValidated: "false",
  hasFundsForThreeMonths: "false",
  teamStrength: "false",
  amount: "50",
  website: "",
  totalFounders: "",
};
const EvaluationForm = () => {
  const btnRef = useRef(null); // Reference for button
  const paymentBtnRef = useRef(null); // Reference for button
  const [isSubmitting, setIsSubmitting] = useState(false); // New state variable
  const [isPDFReady, setIsPDFReady] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [messageData, setMessageData] = useState({
    type: "",
    message: "",
  }); // New state variable message
  const [errors, setErrors] = useState({});
  const [isFormSubmitted, setIsFormSubmitted] = useState(false); // New state variable
  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => {
    if (isPDFReady && submittedData) {
      const generatePDF = async () => {
        try {
          const doc = <MyDocument formData={submittedData} />;
          if (!doc) throw new Error("MyDocument is not defined");
          const blob = await pdf(doc).toBlob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "Startup_Evaluation_Report.pdf";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);

          // ✅ Reset state to prevent re-renders
          setIsPDFReady(false);
          setSubmittedData(null);
        } catch (error) {
          console.error("PDF generation error:", error);
        }
      };

      generatePDF();
    }
  }, [isPDFReady, submittedData]); // ✅ Runs only when needed

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    // Allow only numbers and a single decimal for burnRate & currentRevenueUsd & runwayLeftMonths & seedCapital
    if (
      name === "burnRate" ||
      name === "currentRevenueUsd" ||
      name === "runwayLeftMonths" ||
      name === "seedCapital" ||
      name === "totalFounders"
    ) {
      const regex = /^[0-9]*\.?[0-9]*$/; // Allows only numbers and a single decimal
      if (!regex.test(value)) {
        return; // Prevent updating state if invalid input
      }
    }

    if (name === "contactNumber") {
      const regex = /^[0-9+]*$/; // Allows numbers and (+)sign
      if (!regex.test(value)) {
        return; // Prevent updating state if invalid input
      }
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleOptionSelect = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Disable the button to prevent multiple submissions
    btnRef.current.setAttribute("disabled", "disabled");
    paymentBtnRef.current.setAttribute("disabled", "disabled");
    try {
      const paymentDetails = await displayRazorpay();
      // console.log(paymentDetails);
      if (!paymentDetails) {
        alert("Payment was not completed.");
        setIsSubmitting(false);
        return;
      }
      // console.log("Payment successful! Proceeding with form submission...");

      // TODO THIS WILL ONLY EXCUTE IF USER COMPLETES PAYMENT
      const response = await fetch(
        `${base_url}/core/lovencoders/startup-evaluation`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json(); // parse JSON response

      if (response.status === 400) {
        let errorMessage = "";
        // console.log(typeof data);

        if (data?.message) {
          errorMessage += ` ${data.message} `;
        }
        if (data?.message && data?.errors) {
          if (typeof data.errors === "string") {
            errorMessage += ` ${data.errors}`;
          } else if (Array.isArray(data.errors)) {
            errorMessage += ` ${data.errors.join(", ")}`;
          } else if (typeof data.errors === "object") {
            errorMessage += Object.entries(data.errors)
              .map(
                ([key, value]) =>
                  `${key}: ${Array.isArray(value) ? value.join(", ") : value}`
              )
              .join("; ");
          }
        } else if (typeof data === "object") {
          errorMessage += Object.entries(data)
            .map(([key, value]) => `${key}: ${value}`)
            .join("; ");
        }
        setMessageData({
          type: "error",
          message: errorMessage,
        });

        setIsFormSubmitted(true);
        return;
      }

      if (response.ok) {
        if (sendTransactionData(paymentDetails, data.startupEvaluationId)) {
          console.log("Transaction passed");
        } else {
          console.log("Transaction failed");
          return;
        }
        setFormData(initialFormState);
        setIsFormSubmitted(true);
        setIsModalOpen(false); // Close the modal after successful submission
        setMessageData({
          type: "success",
          message: "Form submitted successfully!",
          desc: "Please wait while your report has been downloaded.",
        });
        // setFormData(initialFormState);
        // ✅ Store form data for the PDF and trigger the effect
        setSubmittedData(data);
        // AFTER 3 SECONDS, SET PDF READY TO TRUE(PDF WILL DOWNLOAD AUTOMATICALLY)
        setTimeout(() => {
          setIsPDFReady(true);
        }, 3000);
      } else {
        setIsFormSubmitted(true);
        setMessageData({
          type: "error",
          message: `Something went wrong.${data?.message || "Unknown Error"}`,
        });
      }
    } catch (error) {
      // console.log("Error submitting form", error);
      setIsModalOpen(false);
      setIsFormSubmitted(true);
      setMessageData({
        type: "error",
        message: `Something went wrong. ${error?.message || "Unknown error"}`,
      });
      // Enable the button after form submission
      btnRef?.current?.removeAttribute("disabled");
      paymentBtnRef?.current?.removeAttribute("disabled");
    }
    // Enable the button after form submission
    btnRef?.current?.removeAttribute("disabled");
    paymentBtnRef?.current?.removeAttribute("disabled");
    setIsSubmitting(false);
  };
  const handleCloseMessage = () => {
    setIsFormSubmitted(!isFormSubmitted);
    setIsSubmitting(false);
  };
  function loadScript(src) {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  }

  async function displayRazorpay() {
    // THIS WILL CREATE ORDER FOR PAYMENT
    let order = await createOrder(paymentAmount);
    // console.log(order);

    if (!order) {
      alert("Razorpay failed to create order!!");
      return null;
    }

    const res = await loadScript(
      "https://checkout.razorpay.com/v1/checkout.js"
    );

    if (!res) {
      console.error("Razorpay failed to load!!");
      return null;
    }

    return new Promise((resolve, reject) => {
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: paymentAmount,
        currency: "INR",
        name: "LovenCoders",
        description: "Payment for evaluation report",
        image: "/lovencoders.png",
        order_id: order?.id,
        handler: async function (response) {
          const isVerified = await verifyPayment(response.razorpay_payment_id);
          if (isVerified) {
            console.error("Payment Verified!");
            resolve(response);
          } else {
            console.error("Payment Verification Failed!");
            reject({ message: "Payment Verification Failed" });
          }
        },
        notes: {
          address: "LovenCoders,Pune,Maharashtra",
        },
        theme: {
          color: "#3399cc",
        },
        modal: {
          ondismiss: function () {
            setIsModalOpen(false);
            // console.log("Payment popup closed by user");
            reject({
              message: "Payment not successfull.",
            });
          },
        },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    });
  }
  const handlePayment = (event) => {
    event.preventDefault(); // Prevent default form submission
    let newErrors = {};
    // VALIDATE PHONE NUMBER
    if (!isValidEmail(formData.founderEmail)) {
      newErrors.founderEmail = "Invalid email";
    }
    // VALIDATE PHONE NUMBER
    if (!isValidPhoneNumber(formData.contactNumber)) {
      newErrors.contactNumber = "Invalid phone number";
    }
    // VALIDATE WEBSITE
    if (!isValidURL(formData.website)) {
      newErrors.website = "Invalid website URL";
    }
    if (formData.startupStage === "") {
      newErrors.startupStage = "Value cannot be zero";
    }

    // CHECK FOR OTHER EMPTY FIELDS (OPTIONAL)
    Object.keys(formData).forEach((key) => {
      if (!formData[key] && key !== "amount") {
        newErrors[key] = "This field is required";
      }
    });
    // IF ERRORS EXIST, UPDATE STATE & RETURN
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsModalOpen(false);
      setIsFormSubmitted(true);
      setMessageData({
        type: "error",
        message: "Please fix the errors before submitting.",
      });
      return;
    }
    // CLEAR ERRORS IF FORM IS VALID
    setErrors({});
    setIsModalOpen(true);
  };
  return (
    <div className={styles.formContainer}>
      {/* Render modal conditionally */}
      <PaymentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        handleSubmit={handleSubmit}
        ref={paymentBtnRef}
      />
      {isSubmitting && <Loading />}
      {isFormSubmitted && (
        <Message data={messageData} handleClick={handleCloseMessage} />
      )}
      <div className={styles.formTitle}>
        Your premium startup evaluation report
      </div>
      <div className={styles.formDesc}>
        Wondering where your startup stands? Take our <b>premium Startup</b>{" "}
        Stage Evaluation Test for a personalized report on your business,
        growth, and key improvements.
      </div>
      <form onSubmit={handlePayment}>
        {/* BASIC INFORMATION */}
        <div className={styles.formSec}>
          <div className={styles.formSecTitle}>Basic Information</div>
          <div className={styles.formControlContainer}>
            <div className={styles.formFieldContainer}>
              <label htmlFor="name">
                Full Name <span className={styles.textDanger}>*</span>
              </label>
              <input
                type="text"
                minLength={0}
                maxLength={50}
                name="founderName"
                id="founderName"
                placeholder="Enter Full Name"
                value={formData.founderName}
                onChange={handleChange}
                className={`${errors.founderName ? "border-red-500" : ""}`}
              />
              {errors.founderName && (
                <p className="text-red-500">{errors.founderName}</p>
              )}
            </div>
            <div className={styles.formFieldContainer}>
              <label htmlFor="address">
                Email Address <span className={styles.textDanger}>*</span>
              </label>
              <input
                type="email"
                name="founderEmail"
                id="founderEmail"
                placeholder="Enter Email Address"
                value={formData.founderEmail}
                onChange={handleChange}
                className={`${errors.founderEmail ? "border-red-500" : ""}`}
              />
              {errors.founderEmail && (
                <p className="text-red-500">{errors.founderEmail}</p>
              )}
            </div>
          </div>
          <div className={styles.formControlContainer}>
            <div className={styles.formFieldContainer}>
              <label htmlFor="startupName">
                Startup Name <span className={styles.textDanger}>*</span>
              </label>
              <input
                type="text"
                name="startupName"
                id="startupName"
                placeholder="Enter Startup Name"
                value={formData.startupName}
                onChange={handleChange}
                className={`${errors.startupName ? "border-red-500" : ""}`}
              />
              {errors.startupName && (
                <p className="text-red-500">{errors.startupName}</p>
              )}
            </div>
            <div className={styles.formFieldContainer}>
              <label htmlFor="startupLocation">
                Startup Location <span className={styles.textDanger}>*</span>
              </label>
              <input
                type="text"
                name="address"
                id="address"
                placeholder="Enter Startup Location"
                value={formData.address}
                onChange={handleChange}
                className={`${errors.address ? "border-red-500" : ""}`}
              />
              {errors.address && (
                <p className="text-red-500">{errors.address}</p>
              )}
            </div>
          </div>
          <div className={styles.formControlContainer}>
            <div className={styles.formFieldContainer}>
              <label htmlFor="startupName">
                Contact Number <span className={styles.textDanger}>*</span>
              </label>
              <input
                type="text"
                name="contactNumber"
                id="contactNumber"
                placeholder="+91XXXXXXXXXX"
                value={formData.contactNumber}
                onChange={handleChange}
                className={`${errors.contactNumber ? "border-red-500" : ""}`}
              />
              {errors.contactNumber && (
                <p className="text-red-500">{errors.contactNumber}</p>
              )}
            </div>
            <div className={styles.formFieldContainer}>
              <label htmlFor="marketingBudget">
                Website <span className={styles.textDanger}>*</span>
              </label>
              <input
                type="text"
                name="website"
                id="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="Enter Website"
                className={`${errors.website ? "border-red-500" : ""}`}
              />
              {errors.website && (
                <p className="text-red-500">{errors.website}</p>
              )}
            </div>
          </div>
          <div className={styles.formControlContainer}>
            <div className={styles.formFieldContainer}>
              <label htmlFor="hasCoFounder">
                Has Co-founder <span className={styles.textDanger}>*</span>
              </label>
              <select
                name="hasCoFounder"
                id="hasCoFounder"
                value={formData.hasCoFounder}
                onChange={handleChange}
              >
                <option value="true">True</option>
                <option value="false">False</option>
              </select>
            </div>
            <div className={styles.formFieldContainer}>
              <label htmlFor="totalFounders">
                Total Founders <span className={styles.textDanger}>*</span>
              </label>
              <input
                type="text"
                name="totalFounders"
                id="totalFounders"
                placeholder="Enter Total Founders"
                value={formData.totalFounders}
                onChange={handleChange}
                className={`${errors.totalFounders ? "border-red-500" : ""}`}
              />
              {errors.totalFounders && (
                <p className="text-red-500">{errors.totalFounders}</p>
              )}
            </div>
          </div>
        </div>

        {/* STARTUP SNAPSHOT */}
        <div className={styles.formSec}>
          <div className={styles.formSecTitle}>Startup Snapshot</div>
          <div className={styles.formControlContainer}>
            <div className={styles.formFieldContainer}>
              <label htmlFor="startupStage">
                Startup Stage <span className={styles.textDanger}>*</span>
              </label>
              <select
                name="startupStage"
                id="startupStage"
                value={formData.startupStage}
                onChange={handleChange}
                className={`${errors.startupStage ? "border-red-500" : ""}`}
              >
                <option value="">Select Stage</option>
                <option value="Idea">Idea</option>
                <option value="Early MVP">Early MVP</option>
                <option value="Growth">Growth</option>
                <option value="Scaling">Scaling</option>
              </select>
              {errors.startupStage && (
                <p className="text-red-500">{errors.startupStage}</p>
              )}
            </div>
            <div className={`${styles.formFieldContainer} ${styles.hidden}`}>
              {/* <label htmlFor="businessModel">
                Business Model <span className={styles.textDanger}>*</span>
              </label>
              <select
                name="businessModel"
                id="businessModel"
                required
                value={formData.businessModel}
                onChange={handleChange}
              >
                <option value="">Select Business Model</option>
                <option value="B2B">B2B</option>
                <option value="B2C">B2C</option>
                <option value="SaaS">SaaS</option>
                <option value="Marketplace">Marketplace</option>
                <option value="Others">Others</option>
              </select> */}
            </div>
          </div>
          <div className={styles.formControlContainer}>
            {/* <div className={styles.formFieldContainer}>
              <label htmlFor="teamSize">
                Team Size <span className={styles.textDanger}>*</span>
              </label>
              <select
                name="teamSize"
                id="teamSize"
                required
                value={formData.teamSize}
                onChange={handleChange}
              >
                <option value="">Select Team Size</option>
                <option value="Solo Founder">Solo Founder</option>
                <option value="2–5">2–5</option>
                <option value="5–10">5–10</option>
                <option value="10–20">10–20</option>
                <option value="20+">20+</option>
              </select>
            </div> */}
            <div className={styles.formFieldContainer}></div>
          </div>
        </div>

        {/* FINANCIAL METRICS */}
        <div className={styles.formSec}>
          <div className={styles.formSecTitle}>Financial Metrics</div>
          <div className={styles.formControlContainer}>
            <div className={styles.formFieldContainer}>
              <label htmlFor="burnRate">
                Monthly Burn Rate <span className={styles.textDanger}>*</span>
              </label>
              <input
                type="text"
                name="burnRate"
                id="burnRate"
                placeholder="Enter Monthly Burn Rate"
                value={formData.burnRate}
                onChange={handleChange}
                className={`${errors.burnRate ? "border-red-500" : ""}`}
              />
              {errors.burnRate && (
                <p className="text-red-500">{errors.burnRate}</p>
              )}
            </div>
            <div className={styles.formFieldContainer}>
              <label htmlFor="currentRevenueUsd">
                Current Revenue <span className={styles.textDanger}>*</span>
              </label>
              <input
                type="text"
                name="currentRevenueUsd"
                id="currentRevenueUsd"
                placeholder="Enter Current Revenue"
                value={formData.currentRevenueUsd}
                onChange={handleChange}
                className={`${
                  errors.currentRevenueUsd ? "border-red-500" : ""
                }`}
              />
              {errors.currentRevenueUsd && (
                <p className="text-red-500">{errors.currentRevenueUsd}</p>
              )}
              {/* <select
                name="currentRevenueUsd"
                id="currentRevenueUsd"
                placeholder="Enter Current Revenue"
                required
                value={formData.currentRevenueUsd}
                onChange={handleChange}
              >
                <option value="">Select Current Revenue</option>
                <option value="None">None</option>
                <option value="1L">&lt;1L</option>
                <option value="1–10L">1–10L</option>
                <option value="10–50L">10–50L</option>
                <option value="50L+">50L+</option>
              </select> */}
            </div>
          </div>
          <div className={styles.formControlContainer}>
            <div className={styles.formFieldContainer}>
              <label htmlFor="runwayLeftMonths">
                Runway Left <span className={styles.textDanger}>*</span>
              </label>
              <div className={styles.formSubHeading}>
                Months capital will last at current burn rate
              </div>
              <input
                type="text"
                name="runwayLeftMonths"
                id="runwayLeftMonths"
                value={formData.runwayLeftMonths}
                onChange={handleChange}
                placeholder="Enter Runway Left"
                className={`${errors.runwayLeftMonths ? "border-red-500" : ""}`}
              />
              {errors.runwayLeftMonths && (
                <p className="text-red-500">{errors.runwayLeftMonths}</p>
              )}
            </div>
            <div className={`${styles.formFieldContainer} ${styles.hidden}`}>
              {/* <label htmlFor="marketingBudget">
                Marketing Budget <span className={styles.textDanger}>*</span>
              </label>
              <div className={styles.formSubHeading}>
                (% of total funds allocated to marketing)
              </div>
              <input
                type="text"
                required
                name="marketingBudget"
                id="marketingBudget"
                placeholder="Enter Marketing Budget(Not In Backend)"
              /> */}
            </div>
          </div>
          <div className={styles.formControlContainer}>
            <div className={styles.formFieldContainer}>
              <label htmlFor="seedCapital">
                Seed Capital <span className={styles.textDanger}>*</span>
              </label>
              <input
                type="text"
                name="seedCapital"
                id="seedCapital"
                value={formData.seedCapital}
                onChange={handleChange}
                placeholder="Enter Seed Capital"
                className={`${errors.seedCapital ? "border-red-500" : ""}`}
              />
              {errors.seedCapital && (
                <p className="text-red-500">{errors.seedCapital}</p>
              )}
            </div>
            <div className={styles.formFieldContainer}></div>
          </div>
        </div>

        {/* TEAM AND EXECUTION */}
        <div className={styles.formSec}>
          <div className={styles.formSecTitle}>Team and Execution</div>
          {[
            {
              question: "Do you have team?",
              field: "teamStrength",
            },
            { question: "Have you built an MVP?", field: "mvpBuilt" },
            {
              question: "Have you validated your idea with real customers?",
              field: "validatedWithCustomers",
            },
          ].map(({ question, field }, index) => (
            <div key={index} className={styles.formAssessmentInputContainer}>
              <div className="form-assessment-input-question">
                {index + 1}. {question}
              </div>
              <div className="form-assessment-options-container">
                <div
                  className={`form-assessment-option ${
                    formData[field] === "false" ? "selected" : ""
                  }`}
                  onClick={() => handleOptionSelect(field, "false")}
                  style={{
                    backgroundColor:
                      formData[field] === "false" ? "rgba(199, 49, 61, 1)" : "",
                    color: formData[field] === "false" ? "white" : "",
                  }}
                >
                  No
                </div>
                <div
                  className={`form-assessment-option ${
                    formData[field] === "true" ? "selected" : ""
                  }`}
                  onClick={() => handleOptionSelect(field, "true")}
                  style={{
                    backgroundColor:
                      formData[field] === "true" ? "rgba(199, 49, 61, 1)" : "",
                    color: formData[field] === "true" ? "white" : "",
                  }}
                >
                  Yes
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* TEAM AND EXECUTION */}
        <div className={styles.formSec}>
          <div className={styles.formSecTitle}>Market and Readiness</div>
          {[
            {
              question: "Have you conducted market research",
              field: "marketResearchDone",
            },
            {
              question: "Do you know your ideal customer persona?",
              field: "idealCustomerIdentified",
            },
            {
              question:
                "Do you have paying customers or LOIs(Letter of Intent)",
              field: "hasPayingCustomers",
            },
          ].map(({ question, field }, index) => (
            <div key={index} className={styles.formAssessmentInputContainer}>
              <div className="form-assessment-input-question">
                {index + 1}. {question}
              </div>
              <div className="form-assessment-options-container">
                <div
                  className={`form-assessment-option ${
                    formData[field] === "false" ? "selected" : ""
                  }`}
                  onClick={() => handleOptionSelect(field, "false")}
                  style={{
                    backgroundColor:
                      formData[field] === "false" ? "rgba(199, 49, 61, 1)" : "",
                    color: formData[field] === "false" ? "white" : "",
                  }}
                >
                  No
                </div>
                <div
                  className={`form-assessment-option ${
                    formData[field] === "true" ? "selected" : ""
                  }`}
                  onClick={() => handleOptionSelect(field, "true")}
                  style={{
                    backgroundColor:
                      formData[field] === "true" ? "rgba(199, 49, 61, 1)" : "",
                    color: formData[field] === "true" ? "white" : "",
                  }}
                >
                  Yes
                </div>
              </div>
            </div>
          ))}
          {/* <div className={styles.formFieldContainerFull}>
            <label htmlFor="runwayLeft">
              4. What problem does your startup solve
            </label>
            <input
              type="text"
              name="runwayLeft"
              id="runwayLeft"
              placeholder="Brief Description"
            />
          </div> */}
        </div>

        {/* STARTUP CHALLENGES */}
        {/* <div className={styles.formSec}>
          <div className={styles.formSecTitle}>Startup Challenges</div>
          <div className={styles.formFieldContainerFull}>
            <label htmlFor="runwayLeft">Biggest Challenge Right Now</label>
            <input
              type="text"
              name="runwayLeft"
              id="runwayLeft"
              placeholder="Select Biggest Challenge"
              value={formData.runwayLeft}
              onChange={handleChange}
            />
            <select
              name="runwayLeft"
              id="runwayLeft"
              placeholder="Enter Current Revenue"
              required
              value={formData.runwayLeft}
              onChange={handleChange}
            >
              <option value="">Select Biggest Challenge</option>
              <option value="Market Validation">Market Validation</option>
              <option value="MVP Building">MVP Building</option>
              <option value="Team Capability">Team Capability</option>
              <option value="Marketing">Marketing</option>
              <option value="Revenue Growth">Revenue Growth</option>
              <option value="Funding">Funding</option>
            </select>
          </div>
        </div> */}

        {/* SELFT ASSESSMENT METRIC */}
        <div className={styles.formSec}>
          <div className={styles.formSecTitle}>Self Assessment Metric</div>
          {[
            {
              question: "Have you validated your target market?",
              field: "targetMarketValidated",
            },
            {
              question: "Do you have enough funds for 3 months of operations?",
              field: "hasFundsForThreeMonths",
            },
          ].map(({ question, field }, index) => (
            <div
              key={index}
              className={styles.formAssessmentInputContainer}
              style={{ marginTop: "20px" }}
            >
              <div className="form-assessment-input-question">
                {index + 1}. {question}
              </div>
              <div className="form-assessment-options-container">
                <div
                  className={`form-assessment-option ${
                    formData[field] === "false" ? "selected" : ""
                  }`}
                  onClick={() => handleOptionSelect(field, "false")}
                  style={{
                    backgroundColor:
                      formData[field] === "false" ? "rgba(199, 49, 61, 1)" : "",
                    color: formData[field] === "false" ? "white" : "",
                  }}
                >
                  No
                </div>
                <div
                  className={`form-assessment-option ${
                    formData[field] === "true" ? "selected" : ""
                  }`}
                  onClick={() => handleOptionSelect(field, "true")}
                  style={{
                    backgroundColor:
                      formData[field] === "true" ? "rgba(199, 49, 61, 1)" : "",
                    color: formData[field] === "true" ? "white" : "",
                  }}
                >
                  Yes
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* STARTUP READINESS INDEX */}
        {/* <div className={styles.formSec}>
          <div className={styles.formSecTitle}>
            Startup Readiness Index (SRI)
          </div>
          <div className={styles.formFieldContainerFull}>
            <label htmlFor="runwayLeft">
              Based on your understanding, how ready is you startup to succeed?
              <span className={styles.formSubHeading}>
                (Scale 1-Not ready to 5-Fully ready)
              </span>
            </label>
            <select
              name="runwayLeft"
              id="runwayLeft"
              placeholder="Enter Current Revenue"
              required
              value={formData.runwayLeft}
              onChange={handleChange}
            >
              <option value="">Select Startup Readiness</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
            </select>
          </div>
        </div> */}

        {/* ADDITIONAL DESCRIPTION */}
        {/* <div className={styles.formSec}>
          <div className={styles.formSecTitle}>Additional Description</div>
          <div className={styles.formFieldContainerFull}>
            <label htmlFor="runwayLeft">
              Describe your problem in detail and the support you seek
            </label>
            <input
              type="text"
              name="runwayLeft"
              id="runwayLeft"
              value={formData.runwayLeft}
              onChange={handleChange}
              placeholder="Brief Description"
            />
          </div>
        </div> */}

        <div className={styles.formBtnContainer}>
          <button ref={btnRef} className={styles.formBtn}>
            Submit
          </button>
          {/* <button type="button" onClick={displayRazorpay}>
            Pay now
          </button> */}
        </div>
      </form>
    </div>
  );
};

export default EvaluationForm;
